import { getUserFriendlyError } from "./errorMessages";

/**
 * Centralized API error handler.
 *
 * Usage:
 *   // In a .then() block:
 *   if (!res.success) {
 *     handleApiError(res, { fallback: "Failed to load cart" });
 *     return;
 *   }
 *
 *   // Or with a toast callback:
 *   handleApiError(res, {
 *     fallback: "Failed to cancel booking",
 *     toast: showToast,
 *   });
 *
 *   // Or in a .catch() block (safety net for unexpected errors):
 *   .catch((err) => handleApiError(err, { fallback: "Something went wrong" }))
 *
 * @param {Object} resOrError  – Normalized API response { success, data, message } OR an Error object
 * @param {Object} [opts]
 * @param {string} [opts.fallback]  – Fallback message when no backend message exists
 * @param {Function} [opts.toast]   – Optional toast function: toast(message, type)
 * @param {Function} [opts.onError] – Optional callback: onError(friendlyMessage)
 * @param {boolean} [opts.log]      – Whether to console.warn (default: process.env.NODE_ENV !== 'production')
 * @returns {string} The user-friendly error message
 */
const handleApiError = (resOrError, opts = {}) => {
  const {
    fallback = "Something went wrong. Please try again.",
    toast = null,
    onError = null,
    log = process.env.NODE_ENV !== "production",
  } = opts;

  // Extract raw message from either a normalised response or an Error object
  let rawMessage;
  if (resOrError instanceof Error) {
    rawMessage = resOrError.message;
  } else {
    rawMessage = resOrError?.message || null;
  }

  const friendlyMessage = rawMessage
    ? getUserFriendlyError(rawMessage)
    : fallback;

  // Dev-only logging
  if (log) {
    console.warn("[API Error]", {
      raw: rawMessage,
      friendly: friendlyMessage,
      httpStatus: resOrError?.httpStatus ?? null,
    });
  }

  // Fire toast if provided
  if (toast) {
    toast(friendlyMessage, "error");
  }

  // Fire custom callback
  if (onError) {
    onError(friendlyMessage);
  }

  return friendlyMessage;
};

export default handleApiError;

